import React from 'react';
import { withStyles } from '@material-ui/core';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { useSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import FormView from 'components/FormView';
import styles from './Details.styles';
import { getProductReceiptDetailForm } from './Details.form';
import { IProductReceiptDetailsHandle, IOrderDetailsProps, setActionBarFn } from './Details.properties';
import { ActionBarIcons, changeConfirmationDialogDefaults } from './Details.constants';
import { useFetchProductReceiptsDetails, useUpdateProductReceiptDetails } from 'api/productReceipts/productReceipts';
import { usePRContextSelector, usePRDispatch } from '../ProductReceipts.context';
import { ProductReceiptsActionTypes } from '../ProductReceipts.actions';

const setActionBarButtons = (setActionBarCallBack: setActionBarFn): void => { setActionBarCallBack(ActionBarIcons); };

const ProductReceiptDetails = (props: IOrderDetailsProps, ref: React.Ref<IProductReceiptDetailsHandle>): JSX.Element => {
  const { classes, changeConfirmationDialog, resetForm, formValues, destroyForm } = props;
  const { setActionBar } = React.useContext(ActionBarContext);

  const ProductReceiptContext = usePRContextSelector<'ProductReceiptContext'>((state) => state.ProductReceiptContext);
  const { data: ProductReceiptDetailsData, isLoading: ProductReceiptDetailsLoading, isFetching: isProductReceiptDetailsFetching } = useFetchProductReceiptsDetails(ProductReceiptContext?.ProductReceiptId);

  const updateProductReceiptDetailsMutation = useUpdateProductReceiptDetails();
  const { showSnackBar } = useSnackBar();
  const onCancelClicked = React.useRef(false);
  const contextDispatch = usePRDispatch();

  const [internalDirty, _setInternalDirty] = React.useState<boolean>(false);

  const isDirty = internalDirty;

  const resetAndNavigateBack = (callBack: () => void) => {
    resetForm();
    destroyForm();
    callBack();
  };

  React.useImperativeHandle(
    ref,
    () => ({
      isChangesSavedOrDiscarded(): boolean {
        if (isDirty) showSnackBar({ variant: 'warning', message: 'Please save or discard changes.' });

        return !isDirty;
      },
      async ProductReceiptDetailsOk(navigateBack: () => void): Promise<void> {
        if (await updateProductReceiptDetails()) {
          resetAndNavigateBack(navigateBack);
        }
      },
      ProductReceiptDetailsCancel(navigateBack: () => void): void {
        onCancelClicked.current = true;
        if (!isDirty) return resetAndNavigateBack(navigateBack);

        changeConfirmationDialog({
          ...changeConfirmationDialogDefaults,
          onOk: () => {
            resetAndNavigateBack(navigateBack);
          },
          onCancel: () => {
            onCancelClicked.current = false;
          }
        });
      }
    }),
    [formValues, isDirty]
  );

  React.useEffect(
    () => { setActionBarButtons(setActionBar); },
    []
  );

  React.useEffect(
    () => {
      if (isDirty) {
        contextDispatch({
          [ProductReceiptsActionTypes.setPreventChangeStatus]: true,
        });
      } else {
        contextDispatch({
          [ProductReceiptsActionTypes.setPreventChangeStatus]: false,
        });
      }
    },
    [isDirty]
  );

  // Reset Form if Context Updates
  React.useEffect(
    () => {
      resetForm();
    },
    [ProductReceiptContext]
  );

  // Update Product Receipt Details
  const updateProductReceiptDetails = React.useCallback(
    async () => {
      const { Status, ...filteredFormValues } = formValues;
      const response = await updateProductReceiptDetailsMutation.mutateAsync({
        ProductReceiptId: ProductReceiptContext?.ProductReceiptId,
        TaxClass: filteredFormValues.TaxClass,
        Comment: filteredFormValues.Comment
      });

      if (response?.Status) {
        _setInternalDirty(false);
      }

      return response?.Status;
    },
    [formValues, updateProductReceiptDetailsMutation, ProductReceiptContext]
  );

  const { ProductReceiptDetails: details } = ProductReceiptDetailsData || {} as any;
  const { CommentEnabled } = details?.inlineObject || {};

  return (
    <div className={classes.formContainer}>
      <FormView
        isLoading={ProductReceiptDetailsLoading || isProductReceiptDetailsFetching}
        formName={'ProductReceiptDetails'}
        schema={getProductReceiptDetailForm(CommentEnabled)}
        includeTabsHeight={true}
        initialValues={details?.inlineObject}
        valuesSchema={details?.inlineObject}
        operationMode={'EDIT'}
        fieldExtensions={{
          TaxClass: {
            onSelectedItemChange: (_, value) => {
              if (value && ProductReceiptDetailsData?.TaxClass !== value) {
                _setInternalDirty(true);
              }
            }
          },
          Comment: {
            onSelectedItemChange: (_, value) => {
              if (value && ProductReceiptDetailsData?.Comment !== value) {
                _setInternalDirty(true);
              }
            }
          }
        }}
      />
    </div>
  );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(ProductReceiptDetails)));
